import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import inventoryService from '../../services/InventoryService.js';
import inventoryUsageService from '../../services/InventoryUsageService.js';
import inventorySKUService from '../../services/InventorySKUService.js';
import { Package, Pencil, ArrowLeft, Plus } from "lucide-react";import { useTranslation } from 'react-i18next';


const ViewInventory = () => {
  const { t } = useTranslation();
  const { inventoryId } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [usageRecords, setUsageRecords] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [skuRecords, setSkuRecords] = useState([]);
  const [loadingSKU, setLoadingSKU] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInventoryItem();
    fetchUsageRecords();
    fetchSKURecords();
  }, [inventoryId]);

  const fetchInventoryItem = async () => {
    try {
      const response = await inventoryService.getById(inventoryId);
      setItem(response.data);
    } catch (err) {
      Alert.error('Failed to load inventory item');
      console.error(err);
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchSKURecords = async () => {
    try {
      setLoadingSKU(true);
      const response = await inventorySKUService.getByInventoryId(inventoryId);
      setSkuRecords(response.data || []);
    } catch (err) {
      console.error('Failed to load SKU records', err);
    } finally {
      setLoadingSKU(false);
    }
  };

  const handleDeleteSKU = async (skuId, skuCode) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete SKU "${skuCode}"? The parent inventory will be updated automatically.`,
      'Delete SKU'
    );
    
    if (confirmed) {
      try {
        await inventorySKUService.delete(skuId);
        Alert.success('SKU deleted and inventory updated!');
        fetchSKURecords();
        fetchInventoryItem(); // Refresh inventory to show updated totals
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete SKU');
      }
    }
  };

  const fetchUsageRecords = async () => {
    try {
      setLoadingUsage(true);
      const response = await inventoryUsageService.getByInventory(inventoryId);
      setUsageRecords(response.data || []);
    } catch (err) {
      console.error('Failed to load usage records', err);
    } finally {
      setLoadingUsage(false);
    }
  };

  const handleDeleteUsage = async (usageId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to delete this usage record? The quantity will be restored to inventory.',
      'Delete Usage Record'
    );
    
    if (confirmed) {
      try {
        await inventoryUsageService.delete(usageId);
        Alert.success('Usage record deleted and inventory restored!');
        fetchUsageRecords();
        fetchInventoryItem(); // Refresh inventory to show updated quantity
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete usage record');
      }
    }
  };

  const getItemImageUrl = () => {
    if (item.item_image_base64) {
      return `data:image/jpeg;base64,${item.item_image_base64}`;
    }
    return null;
  };

  const getInvoiceUrl = () => {
    if (item.invoice_attachment_base64) {
      return `data:application/pdf;base64,${item.invoice_attachment_base64}`;
    }
    return null;
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'In Stock': 'bg-green-100 text-green-700',
      'Low Stock': 'bg-yellow-100 text-yellow-700',
      'Out of Stock': 'bg-red-100 text-red-700',
      'On Order': 'bg-blue-100 text-blue-700',
      'Reserved': 'bg-purple-100 text-purple-700',
      'Damaged': 'bg-orange-100 text-orange-700',
      'Expired': 'bg-gray-100 text-secondary',
      'Discontinued': 'bg-gray-100 text-secondary',
      'Under Maintenance': 'bg-indigo-100 text-indigo-700'
    };
    return statusClasses[status] || 'bg-gray-100 text-secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <div className="max-w-[1850px] mx-auto">
        <Header />
        <div className="flex">
          <Sidebar />
          <div className="flex bg-light w-full mt-16 sm:mt-0">
            <div className="flex-1 flex flex-col overflow-hidden">
              <main className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                  <p className="text-sm sm:text-base text-gray-600">Loading inventory item...</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return null;
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <Package className="w-5 h-5 text-buttonBg" />Inventory Item Details
                  </h1>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  {/* Header Section */}
                  <div className="bg-primary p-3 px-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4">
                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-3">
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-secondary mb-0 break-words">
                          {item.item_name}
                        </h2>
                        <span className={`px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-sm sm:text-sm font-semibold ${getStatusBadgeClass(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                      <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button
                          onClick={() => navigate(`/inventory/edit/${inventoryId}`)}
                          className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-sm sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-semibold whitespace-nowrap"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit Item
                        </button>
                        <button
                          onClick={() => navigate('/inventory')}
                          className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-sm sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-semibold whitespace-nowrap"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Back to Inventory
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">

                    {/* Basic Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Inventory ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.inventory_id}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Item Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.item_name}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Category</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.category}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Sub Category</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.sub_category || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:gap-6 mt-4">
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Description</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.description || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Quantity and Pricing */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Quantity and Pricing
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Quantity</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.quantity} {item.unit}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Unit Price</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatCurrency(item.unit_price)}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Total Value</label>
                          <p className="text-sm sm:text-base text-green-600 font-bold break-all">{formatCurrency(item.total_value)}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Status</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadgeClass(item.status)}`}>
                              {item.status}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Supplier Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Supplier Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Supplier Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.supplier_name || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Supplier Contact</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.supplier_contact || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Attachments Section */}
                    {(item.item_image_base64 || item.invoice_attachment_base64) && (
                    <div>
                        <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Attachments
                        </h3>
                        <div className="flex flex-wrap gap-3">
                        {item.item_image_base64 && (
                            <button
                            onClick={() => setShowImageModal(true)}
                            className="flex items-center gap-2 px-3 py-2 text-sm sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonhoverTxt hover:bg-buttonhoverBg border rounded-sm transition-all duration-300 font-semibold"
                            >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Item Image
                            </button>
                        )}
                        {item.invoice_attachment_base64 && (
                            <button
                            onClick={() => setShowInvoiceModal(true)}
                            className="flex items-center gap-2 px-3 py-2 text-sm sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonhoverTxt hover:bg-buttonhoverBg border rounded-sm transition-all duration-300 font-semibold"
                            >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Invoice
                            </button>
                        )}
                        </div>
                    </div>
                    )}

                    {/* Location and Dates */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Location and Dates
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Warehouse Location</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.warehouse_location}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Purchase Date</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(item.purchase_date)}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Expiry Date</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(item.expiry_date)}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Last Stock Update</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(item.last_stock_update)}</p>
                        </div>
                      </div>
                    </div>

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">{t('common.createdBy')}</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.created_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(item.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Updated By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{item.updated_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm sm:text-sm font-semibold text-secondary mb-1">Last Updated</label>
                          <p className="text-sm sm:text-base sm:text-base text-secondary font-semibold break-all">{formatDate(item.updated_at)}</p>
                        </div>
                      </div>
                    </div>

                    {/* SKU Records Section */}
                    <div>
                      <div className="flex justify-between items-center mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        <h3 className="text-base sm:text-md font-semibold text-secondary">
                          SKU Records ({skuRecords.length})
                        </h3>
                        <button
                          onClick={() => navigate(`/inventory-sku/add/${inventoryId}`)}
                          className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-sm sm:text-base sm:text-sm sm:text-base text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-sm  transition-all duration-300 font-semibold whitespace-nowrap"
                        >
                          <Plus className="w-4 h-4" />
                          Add SKU
                        </button>
                      </div>

                      {loadingSKU ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
                          <p className="text-sm sm:text-base text-gray-600">Loading SKU records...</p>
                        </div>
                      ) : skuRecords.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-md">
                          <p className="text-gray-600">No SKU records found for this item.</p>
                          <button
                            onClick={() => navigate(`/inventory-sku/add/${inventoryId}`)}
                            className="mt-3 text-sm sm:text-base text-orange-500 hover:text-orange-600 font-semibold"
                          >
                            Add first SKU →
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* Desktop Table */}
                          <div className="hidden md:block overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">SKU ID</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">SKU CODE</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">SKU NAME</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">VARIANT</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">QUANTITY</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">UNIT PRICE</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">BIN LOCATION</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">STATUS</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">ACTIONS</th>
                                </tr>
                              </thead>
                              <tbody>
                                {skuRecords.map((record) => (
                                  <tr key={record.sku_id} className="hover:bg-gray-50 border-b">
                                    <td className="px-4 py-3">
                                      <button
                                        onClick={() => navigate(`/inventory-sku/view/${record.sku_id}`)}
                                        className="text-sm sm:text-base font-semibold text-secondary hover:underline"
                                      >
                                        {record.sku_id}
                                      </button>
                                    </td>
                                    <td className="px-4 py-3 text-sm sm:text-base text-secondary font-semibold">{record.sku_code}</td>
                                    <td className="px-4 py-3 text-sm sm:text-base text-secondary font-semibold">{record.sku_name}</td>
                                    <td className="px-4 py-3 text-sm sm:text-base text-secondary font-semibold">
                                      {record.variant_type && record.variant_value 
                                        ? `${record.variant_type}: ${record.variant_value}` 
                                        : 'N/A'}
                                    </td>
                                    <td className="px-4 py-3 text-sm sm:text-base font-semibold text-secondary">{record.quantity} {item.unit}</td>
                                    <td className="px-4 py-3 text-sm sm:text-base font-semibold text-secondary">{formatCurrency(record.unit_price)}</td>
                                    <td className="px-4 py-3 text-sm sm:text-base text-secondary font-semibold">{record.bin_location || 'N/A'}</td>
                                    <td className="px-4 py-3">
                                      <span className={`px-2 py-1 rounded-full text-sm sm:text-base font-semibold ${
                                        record.is_active ? 'text-secondary' : 'text-secondary'
                                      }`}>
                                        {record.is_active ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => navigate(`/inventory-sku/view/${record.sku_id}`)}
                                          className="text-blue-600 hover:text-blue-700 text-sm sm:text-base"
                                          title={t("common.view")}
                                        >
                                          👁️
                                        </button>
                                        <button
                                          onClick={() => navigate(`/inventory-sku/edit/${record.sku_id}`)}
                                          className="text-orange-600 hover:text-orange-700 text-sm sm:text-base"
                                          title={t("common.edit")}
                                        >
                                          ✏️
                                        </button>
                                        <button
                                          onClick={() => handleDeleteSKU(record.sku_id, record.sku_code)}
                                          className="text-red-600 hover:text-red-700 text-sm sm:text-base"
                                          title={t("common.delete")}
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Mobile Cards */}
                          <div className="md:hidden space-y-3">
                            {skuRecords.map((record) => (
                              <div key={record.sku_id} className="bg-gray-50 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-3">
                                  <button
                                    onClick={() => navigate(`/inventory-sku/view/${record.sku_id}`)}
                                    className="font-semibold text-orange-600 hover:text-orange-700"
                                  >
                                    {record.sku_code}
                                  </button>
                                  <span className={`text-sm sm:text-base px-2 py-1 rounded-full font-semibold ${
                                    record.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-secondary'
                                  }`}>
                                    {record.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-2 text-sm sm:text-base mb-3">
                                  <div>
                                    <span className="text-gray-600">Name:</span>
                                    <p className="font-semibold">{record.sku_name}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Variant:</span>
                                    <p className="font-semibold">
                                      {record.variant_type && record.variant_value 
                                        ? `${record.variant_type}: ${record.variant_value}` 
                                        : 'N/A'}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Quantity:</span>
                                    <p className="font-semibold">{record.quantity} {item.unit}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Price:</span>
                                    <p className="font-semibold text-green-600">{formatCurrency(record.unit_price)}</p>
                                  </div>
                                  <div className="col-span-2">
                                    <span className="text-gray-600">Bin Location:</span>
                                    <p className="font-semibold">{record.bin_location || 'N/A'}</p>
                                  </div>
                                </div>

                                <div className="flex gap-2">
                                  <button
                                    onClick={() => navigate(`/inventory-sku/view/${record.sku_id}`)}
                                    className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm sm:text-base font-semibold"
                                  >
                                    👁️ View
                                  </button>
                                  <button
                                    onClick={() => navigate(`/inventory-sku/edit/${record.sku_id}`)}
                                    className="flex-1 px-3 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 text-sm sm:text-base font-semibold"
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSKU(record.sku_id, record.sku_code)}
                                    className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm sm:text-base font-semibold"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Usage Records Section */}
                    <div>
                      <div className="flex justify-between items-center mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        <h3 className="text-base sm:text-md font-semibold text-secondary">
                          Usage Records ({usageRecords.length})
                        </h3>
                        <button
                          onClick={() => navigate(`/inventory-usage/add/${inventoryId}`)}
                          className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-sm sm:text-base sm:text-sm sm:text-base text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-sm  transition-all duration-300 font-semibold whitespace-nowrap"
                        >
                          <Plus className="w-4 h-4" />
                          Record Usage
                        </button>
                      </div>

                      {loadingUsage ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
                          <p className="text-sm sm:text-base text-gray-600">Loading usage records...</p>
                        </div>
                      ) : usageRecords.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-md">
                          <p className="text-gray-600">No usage records found for this item.</p>
                          <button
                            onClick={() => navigate(`/inventory-usage/add/${inventoryId}`)}
                            className="mt-3 text-sm sm:text-base text-orange-500 hover:text-orange-600 font-semibold"
                          >
                            Record first usage →
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* Desktop Table */}
                          <div className="hidden md:block overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">USAGE ID</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">DATE</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">TYPE</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">PURPOSE</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">QUANTITY</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">COST</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">USED BY</th>
                                  <th className="px-4 py-3 text-left text-sm sm:text-base font-semibold text-secondary border-b">ACTIONS</th>
                                </tr>
                              </thead>
                              <tbody>
                                {usageRecords.map((record) => (
                                  <tr key={record.usage_id} className="hover:bg-gray-50 border-b">
                                    <td className="px-4 py-3">
                                      <button
                                        onClick={() => navigate(`/inventory-usage/view/${record.usage_id}`)}
                                        className="text-sm sm:text-base font-semibold text-secondary hover:underline"
                                      >
                                        {record.usage_id}
                                      </button>
                                    </td>
                                    <td className="px-4 py-3 text-sm sm:text-base text-secondary">{formatDate(record.usage_date)}</td>
                                    <td className="px-4 py-3">
                                      <span className="px-2 py-1 text-secondary text-sm sm:text-base font-semibold">
                                        {record.usage_type}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="px-2 py-1 text-secondary text-sm sm:text-base font-semibold">
                                        {record.usage_purpose}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm sm:text-base text-secondary">{record.quantity_used} {record.unit}</td>
                                    <td className="px-4 py-3 text-sm sm:text-base font-semibold text-secondary">{formatCurrency(record.total_cost)}</td>
                                    <td className="px-4 py-3 text-sm sm:text-base text-secondary">{record.used_by_name}</td>
                                    <td className="px-4 py-3">
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => navigate(`/inventory-usage/view/${record.usage_id}`)}
                                          className="text-blue-600 hover:text-blue-700 text-sm sm:text-base"
                                          title={t("common.view")}
                                        >
                                          👁️
                                        </button>
                                        <button
                                          onClick={() => navigate(`/inventory-usage/edit/${record.usage_id}`)}
                                          className="text-orange-600 hover:text-orange-700 text-sm sm:text-base"
                                          title={t("common.edit")}
                                        >
                                          ✏️
                                        </button>
                                        <button
                                          onClick={() => handleDeleteUsage(record.usage_id)}
                                          className="text-red-600 hover:text-red-700 text-sm sm:text-base"
                                          title={t("common.delete")}
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Mobile Cards */}
                          <div className="md:hidden space-y-3">
                            {usageRecords.map((record) => (
                              <div key={record.usage_id} className="bg-gray-50 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-3">
                                  <button
                                    onClick={() => navigate(`/inventory-usage/view/${record.usage_id}`)}
                                    className="font-semibold text-orange-600 hover:text-orange-700"
                                  >
                                    {record.usage_id}
                                  </button>
                                  <span className="text-sm sm:text-base text-gray-600">{formatDate(record.usage_date)}</span>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-2 text-sm sm:text-base mb-3">
                                  <div>
                                    <span className="text-gray-600">Type:</span>
                                    <p className="font-semibold">
                                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm sm:text-base">
                                        {record.usage_type}
                                      </span>
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Purpose:</span>
                                    <p className="font-semibold">
                                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm sm:text-base">
                                        {record.usage_purpose}
                                      </span>
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Quantity:</span>
                                    <p className="font-semibold">{record.quantity_used} {record.unit}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Cost:</span>
                                    <p className="font-semibold text-green-600">{formatCurrency(record.total_cost)}</p>
                                  </div>
                                  <div className="col-span-2">
                                    <span className="text-gray-600">Used By:</span>
                                    <p className="font-semibold">{record.used_by_name}</p>
                                  </div>
                                </div>

                                <div className="flex gap-2">
                                  <button
                                    onClick={() => navigate(`/inventory-usage/view/${record.usage_id}`)}
                                    className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm sm:text-base font-semibold"
                                  >
                                    👁️ View
                                  </button>
                                  <button
                                    onClick={() => navigate(`/inventory-usage/edit/${record.usage_id}`)}
                                    className="flex-1 px-3 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 text-sm sm:text-base font-semibold"
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteUsage(record.usage_id)}
                                    className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm sm:text-base font-semibold"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Document Modals */}
      <DocumentModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        documentUrl={getItemImageUrl()}
        documentType="Item Image"
      />

      <DocumentModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        documentUrl={getInvoiceUrl()}
        documentType="Invoice"
      />
    </div>
  );
};

export default ViewInventory;